// BackendPageForContact.js — the WorksForYou contact page.
//
// Reads contact data from <ConfigContext> (App.js fetches /api/page/contact once
// and provides it globally — same data the footer's LinkedIn icon uses).
//
// Sections rendered:
//   1. Header   — big title + intro line
//   2. Channels — email (full width) + LinkedIn / Based in (half each)
//   3. Form     — 3 fields → POSTs to /api/page/contact/send, which emails us
//                 (no mailto / client opening)

import React, { useContext, useState } from 'react';
import axios from 'axios';
import { ConfigContext } from '../../App';
import {
  Navigation,
  Footer,
  getText,
  getStyle,
  getBottomMargin,
  RT,
} from '../../myReactLib/MainBlocks';

function BackendPageForContact() {
  const { config, homepageData, contactData } = useContext(ConfigContext);

  if (!homepageData) return null;
  const menuBar = homepageData.menu_bar;
  const prefix  = config.PREFIX;

  if (!contactData) {
    return (
      <div className="w4u-page">
        <Navigation menuBar={menuBar} prefix={prefix} />
        <main className="w4u-main">
          <div className="w4u-loading">Loading…</div>
        </main>
        <Footer menuBar={menuBar} config={config} contactData={contactData} footer={homepageData.footer} />
      </div>
    );
  }

  return (
    <div className="w4u-page">
      <Navigation menuBar={menuBar} prefix={prefix} />
      <main className="w4u-main">
        <ContactHeader   header={contactData.header} />
        <ContactChannels channels={contactData.channels} />
        <ContactForm     form={contactData.form} apiUrl={config.API_URL} />
      </main>
      <Footer menuBar={menuBar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

// ----- Sub-sections -----

const ContactHeader = ({ header }) => {
  if (!header) return null;
  return (
    <section className="w4u-contact__header" style={getBottomMargin(header.bottom_margin)}>
      {header.title && (
        <h1 className="w4u-contact__title" style={getStyle(header.title)}>
          {RT(header.title)}
        </h1>
      )}
      {header.subtitle && (
        <p className="w4u-contact__subtitle" style={getStyle(header.subtitle)}>
          {RT(header.subtitle)}
        </p>
      )}
    </section>
  );
};

// channels is a named dict ({ email:{…}, linkedin:{…}, based_in:{…} }); render its values in order.
const ContactChannels = ({ channels }) => {
  const list = channels ? Object.values(channels) : [];
  if (list.length === 0) return null;
  return (
    <section className="w4u-contact__channels">
      {list.map((c, i) => {
        const inner = (
          <>
            <span className="w4u-contact__channel-label" style={getStyle(c.label)}>{RT(c.label)}</span>
            <span className="w4u-contact__channel-value" style={getStyle(c.value)}>{RT(c.value)}</span>
          </>
        );
        return c.href ? (
          <a
            key={i}
            href={c.href}
            className="w4u-contact__channel"
            target={c.href.startsWith('http') ? '_blank' : undefined}
            rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {inner}
          </a>
        ) : (
          <div key={i} className="w4u-contact__channel w4u-contact__channel--static">
            {inner}
          </div>
        );
      })}
    </section>
  );
};

const ContactForm = ({ form, apiUrl }) => {
  const [values, setValues] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');   // idle | sending | sent | error

  if (!form) return null;

  // input fields = everything under `fields` except the submit button entry
  const allFields = form.fields || {};
  const inputFields = Object.entries(allFields).filter(([key]) => key !== 'submit_button');
  const submitLabel = getText(allFields.submit_button) || 'Send';

  const onChange = (field) => (e) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  // submit → POST to the backend, which sends us the email; no mail client opens
  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await axios.post(`${apiUrl}/page/contact/send`, {
        name: values.name,
        email: values.email,
        message: values.message,
      });
      setStatus(res.data && res.data.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  };

  // once sent, show a thank-you instead of the form
  if (status === 'sent') {
    return (
      <section className="w4u-contact__form-wrap">
        <p className="w4u-contact__sent">Thanks — your message is on its way. I'll get back to you shortly.</p>
      </section>
    );
  }

  return (
    <section className="w4u-contact__form-wrap" style={getBottomMargin(form.bottom_margin)}>
      {form.heading && (
        <h2 className="w4u-contact__form-heading" style={getStyle(form.heading)}>
          {RT(form.heading)}
        </h2>
      )}
      {form.subheading && (
        <p className="w4u-contact__form-subheading" style={getStyle(form.subheading)}>
          {RT(form.subheading)}
        </p>
      )}

      <form className="w4u-contact__form" onSubmit={onSubmit}>
        {inputFields.map(([key, f]) => (
          <div key={key} className="w4u-contact__field">
            <label htmlFor={`w4u-contact-${key}`} className="w4u-contact__label" style={getStyle(f.label)}>
              {getText(f.label) ? RT(f.label) : key}
              {f.required && <span className="w4u-contact__required" aria-hidden="true"> *</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                id={`w4u-contact-${key}`}
                className="w4u-contact__input w4u-contact__input--textarea"
                placeholder={getText(f.placeholder)}
                required={!!f.required}
                value={values[key] || ''}
                onChange={onChange(key)}
                rows={5}
              />
            ) : (
              <input
                id={`w4u-contact-${key}`}
                className="w4u-contact__input"
                type={f.type || 'text'}
                placeholder={getText(f.placeholder)}
                required={!!f.required}
                value={values[key] || ''}
                onChange={onChange(key)}
              />
            )}
          </div>
        ))}
        <button type="submit" className="w4u-btn-orange w4u-contact__submit" style={getStyle(allFields.submit_button)} disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : submitLabel}
        </button>
        {status === 'error' && (
          <p className="w4u-contact__sent">
            Something went wrong sending your message. Please email us directly at
            {' '}<a href="mailto:worksforyou.solutions@gmail.com" className="w4u-orange">worksforyou.solutions@gmail.com</a>.
          </p>
        )}
      </form>
    </section>
  );
};

export default BackendPageForContact;
